import { getSystemApiBase } from '../../config/apiBase';
import { systemRequest } from '../../utils/systemRequest';

/**
 * 生成团队编号
 * 格式：年月日+当天创建的团队顺序（如：2025102701）
 * @returns Promise<string>
 */
export const generateGroupNo = async (): Promise<string> => {
  try {
    // 获取当前日期
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    const datePrefix = `${year}${month}${day}`;
    
    // 获取团队列表
    const groupListResponse = await getGroupListApi();
    
    // 处理响应格式（兼容数组和对象格式）
    const groupList = Array.isArray(groupListResponse) 
      ? groupListResponse 
      : (groupListResponse?.data || []);
    
    // 筛选出当天创建的团队
    const todayGroups = groupList.filter((group: Profile.GroupItem) => {
      const groupDate = group.group_no.substring(0, 8);
      return groupDate === datePrefix;
    });
    
    // 计算当天的团队数量，生成下一个序号
    const nextSequence = String(todayGroups.length + 1).padStart(2, '0');
    const groupNo = `${datePrefix}${nextSequence}`;
    
    console.log(`生成团队编号: ${groupNo} (当天已有 ${todayGroups.length} 个团队)`);
    return groupNo;
  } catch (error: any) {
    console.error('生成团队编号失败:', error);
    // 如果获取团队列表失败，使用默认格式
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    return `${year}${month}${day}01`;
  }
};

/**
 * 获取团队列表
 * @param params 查询参数（可选）
 * @returns Promise<Profile.GroupListResult>
 */
export const getGroupListApi = async (params?: { 
  name?: string; 
  sort?: 'asc' | 'desc';
  page?: number;
  per_page?: number;
}): Promise<Profile.GroupListResult> => {
  try {
    const queryParams = new URLSearchParams();
    
    if (params?.name) {
      queryParams.append('name', params.name);
    }
    
    if (params?.sort) {
      queryParams.append('sort', params.sort);
    }
    
    if (params?.page !== undefined) {
      queryParams.append('page', params.page.toString());
    }
    
    if (params?.per_page !== undefined) {
      queryParams.append('per_page', params.per_page.toString());
    }
    
    const queryString = queryParams.toString();
    const url = queryString ? `/group/list?${queryString}` : '/group/list';
    
    console.log('调用API:', url);
    const response = await systemRequest<Profile.GroupListResult>(url, {
      method: 'GET'
    });
    console.log('团队列表API响应:', response);
    
    return response;
  } catch (error: any) {
    // 构建控制台错误信息
    const errorInfo = {
      'API调用失败': '/group/list',
      '状态码': error?.status || 'N/A',
      '状态文本': error?.statusText || 'N/A',
      '服务器响应': error?.response || 'N/A',
      '错误消息': error?.message || 'N/A',
      '请求URL': `/translate/system/group/list`,
      '请求方法': 'GET'
    };
    
    console.error('=== 团队列表API调用错误详情 ===');
    console.table(errorInfo);
    console.error('完整错误对象:', error);
    
    // 直接抛出错误
    throw error;
  }
};

/**
 * 创建团队
 * @param groupNo 团队编号（可选，如果不提供则自动生成）
 * @returns Promise<Profile.CreateGroupResponse>
 */
export const createGroupApi = async (groupNo?: string): Promise<Profile.CreateGroupResponse> => {
  try {
    // 如果没有提供团队编号，则自动生成
    const finalGroupNo = groupNo || await generateGroupNo();
    
    const requestData: Profile.CreateGroupRequest = {
      group_no: finalGroupNo
    };
    
    console.log('调用API: /group/create', requestData);
    const response = await systemRequest<Profile.CreateGroupResponse>(`/group/create`, {
      method: 'POST',
      body: requestData
    });
    console.log('创建团队API响应:', response);
    
    return response;
  } catch (error: any) {
    // 构建控制台错误信息
    const errorInfo = {
      'API调用失败': '/group/create',
      '状态码': error?.status || 'N/A',
      '状态文本': error?.statusText || 'N/A',
      '服务器响应': error?.response || 'N/A',
      '错误消息': error?.message || 'N/A',
      '请求URL': `/translate/system/group/create`,
      '请求方法': 'POST',
      '请求数据': groupNo || 'auto-generated'
    };
    
    console.error('=== 创建团队API调用错误详情 ===');
    console.table(errorInfo);
    console.error('完整错误对象:', error);
    
    // 直接抛出错误
    throw error;
  }
};

/**
 * 删除团队
 * @param groupId 团队ID
 * @returns Promise<void>
 */
export const deleteGroupApi = async (groupId: number): Promise<void> => {
  try {
    console.log('调用API: /group/delete', { groupId });
    
    // 直接使用fetch，避免JSON解析问题
    const token = localStorage.getItem("token");
    const url = `${getSystemApiBase()}/group/${groupId}`;
    
    const response = await fetch(url, {
      method: 'DELETE',
      headers: {
        'Authorization': token?.startsWith('Bearer ') ? token : `Bearer ${token}`,
      }
    });
    
    if (!response.ok) {
      throw new Error(`删除失败: ${response.status} ${response.statusText}`);
    }
    
    console.log('删除团队API成功:', groupId);
  } catch (error: any) {
    // 检查是否是JSON解析错误，如果是则忽略（删除成功但响应为空）
    if (error.message && error.message.includes('Unexpected end of JSON input')) {
      console.log('删除团队成功（响应为空）:', groupId);
      return; // 删除成功，不抛出错误
    }
    
    // 构建控制台错误信息
    const errorInfo = {
      'API调用失败': `/group/${groupId}`,
      '状态码': error?.status || 'N/A',
      '状态文本': error?.statusText || 'N/A',
      '服务器响应': error?.response || 'N/A',
      '错误消息': error?.message || 'N/A',
      '请求URL': `/translate/system/group/${groupId}`,
      '请求方法': 'DELETE',
      '团队ID': groupId
    };
    
    console.error('=== 删除团队API调用错误详情 ===');
    console.table(errorInfo);
    console.error('完整错误对象:', error);
    
    // 直接抛出错误
    throw error;
  }
};

/**
 * 设置审核者
 * @param groupNo 团队编号
 * @param assessorIds 审核者ID数组
 * @returns Promise<Profile.AssignAssessorResponse>
 */
export const assignAssessorApi = async (
  groupNo: string, 
  assessorIds: number[]
): Promise<Profile.AssignAssessorResponse> => {
  try {
    const requestData: Profile.AssignAssessorRequest = {
      group_no: groupNo,
      assessor_ids: assessorIds
    };
    
    console.log('调用API: /group/assign', requestData);
    const response = await systemRequest<Profile.AssignAssessorResponse>(`/group/assign`, {
      method: 'POST',
      body: requestData
    });
    console.log('设置审核者API响应:', response);
    
    return response;
  } catch (error: any) {
    // 构建控制台错误信息
    const errorInfo = {
      'API调用失败': '/group/assign',
      '状态码': error?.status || 'N/A',
      '状态文本': error?.statusText || 'N/A',
      '服务器响应': error?.response || 'N/A',
      '错误消息': error?.message || 'N/A',
      '请求URL': `/translate/system/group/assign`,
      '请求方法': 'POST',
      '请求数据': { groupNo, assessorIds }
    };
    
    console.error('=== 设置审核者API调用错误详情 ===');
    console.table(errorInfo);
    console.error('完整错误对象:', error);
    
    // 直接抛出错误
    throw error;
  }
};

/**
 * 根据团队编号获取团队成员列表
 * @param groupNo 团队编号
 * @param noPagination 是否不分页（用于设置审核者下拉框等需要获取全部成员的场景）
 * @returns Promise<Profile.GroupMember[]>
 */
export const getGroupMembersByGroupNoApi = async (
  groupNo: string,
  noPagination?: boolean
): Promise<Profile.GroupMember[]> => {
  try {
    let groupListResponse: Profile.GroupListResult;
    
    if (noPagination) {
      // 需要获取全部数据时，先获取第一页数据以获取总数
      const firstPageResponse = await getGroupListApi({ page: 1 });
      
      let total = 0;
      let firstPageData: Profile.GroupItem[] = [];
      
      // 兼容不同的响应格式，获取总数和数据
      if (Array.isArray(firstPageResponse)) {
        firstPageData = firstPageResponse;
        total = firstPageResponse.length;
      } else if (firstPageResponse && typeof firstPageResponse === 'object' && 'pagination' in firstPageResponse) {
        total = (firstPageResponse as any).pagination?.total || 0;
        if ('data' in firstPageResponse && Array.isArray((firstPageResponse as any).data)) {
          firstPageData = (firstPageResponse as any).data;
        }
      }
      
      // 如果第一页已经包含所有数据，直接使用第一页数据
      if (firstPageData.length >= total || total === 0) {
        groupListResponse = firstPageResponse;
      } else {
        // 否则，根据总数获取所有团队数据
        groupListResponse = await getGroupListApi({ per_page: total });
      }
    } else {
      // 使用默认分页（不传递 per_page 参数）
      groupListResponse = await getGroupListApi();
    }
    
    // 处理响应格式（兼容数组和对象格式）
    const groupList = Array.isArray(groupListResponse) 
      ? groupListResponse 
      : (groupListResponse?.data || []);
    
    // 根据团队编号查找对应的团队
    const targetGroup = groupList.find((group: Profile.GroupItem) => group.group_no === groupNo);
    
    if (!targetGroup) {
      throw new Error(`未找到团队编号为 ${groupNo} 的团队`);
    }
    
    console.log(`找到团队 ${groupNo}，成员数量: ${targetGroup.members.length}`);
    return targetGroup.members;
  } catch (error: any) {
    console.error('获取团队成员失败:', error);
    throw error;
  }
};

/**
 * 加入团队
 * @param groupNo 团队编号
 * @returns Promise<Profile.JoinGroupResponse>
 */
export const joinGroupApi = async (groupNo: string): Promise<Profile.JoinGroupResponse> => {
  try {
    const requestData: Profile.JoinGroupRequest = {
      group_no: groupNo
    };
    
    console.log('调用API: /group/join', requestData);
    const response = await systemRequest<Profile.JoinGroupResponse>(`/group/join`, {
      method: 'POST',
      body: requestData
    });
    console.log('加入团队API响应:', response);
    
    return response;
  } catch (error: any) {
    // 构建控制台错误信息
    const errorInfo = {
      'API调用失败': '/group/join',
      '状态码': error?.status || 'N/A',
      '状态文本': error?.statusText || 'N/A',
      '服务器响应': error?.response || 'N/A',
      '错误消息': error?.message || 'N/A',
      '请求URL': `/translate/system/group/join`,
      '请求方法': 'POST',
      '请求数据': { groupNo }
    };
    
    console.error('=== 加入团队API调用错误详情 ===');
    console.table(errorInfo);
    console.error('完整错误对象:', error);
    
    // 直接抛出错误
    throw error;
  }
};
